import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/DashboardLayout";
import { KCard } from "@/components/KCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExamResultReport } from "@/components/ExamResultReport";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, BookOpen, CheckCircle2, Clock, Loader2, XCircle, Flag, Award, AlertCircle, Volume2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { shuffleScenarioQuestionGroups } from "@/lib/shuffleSeeded";
import { useAuth } from "@/auth/AuthContext";
import { getMyEnrollments, getCourseChapters, resetChapterProgress, getCourse } from "@/api/lms/courses";
import { fetchProfile } from "@/api/userAccount";
import {
  buildMcqDisplayPairs,
  migrateLoadedMcqToIndex,
  normalizeMcqAnswersForApi,
  isQuestionAnswered,
} from "@/lib/mcqAnswer";
import { mcqDisplayBadge } from "@/lib/mcqOptionIds";
import {
  getExamQuestions,
  startExam,
  submitExam,
  saveExamProgress,
  getCourseExams,
  getMyAttempts,
  getAttemptReview,
  getSignedAudioUrl,
  type ExamAttempt,
  type ExamQuestion,
  type QuestionScenario,
} from "@/api/lms/exams";
import { renderFurigana } from "@/lib/japaneseHelpers";
import { NoCopyQuestionStem } from "@/components/NoCopyQuestionStem";

import { CustomSecureAudioPlayer } from "@/components/CustomSecureAudioPlayer";
import { ExamReviewPanel } from "@/components/ExamReviewPanel";
import type { ReviewableQuestion } from "@/components/ExamReviewPanel";
import { ExamSecurityWrapper } from "@/components/ExamSecurityWrapper";
import { QuestionCard } from "@/components/exam/QuestionCard";
import { ScenarioBlock } from "@/components/exam/ScenarioBlock";

// getCircleBadge moved to QuestionCard

function getPlayLimitFromUrl(url: string | null, fallback: number): number {
    if (!url) return fallback;
    try {
        const match = url.match(/[?&]playLimit=([^&]+)/);
        if (match) {
            if (match[1] === "unlimited") return 0;
            const parsed = parseInt(match[1], 10);
            if (!isNaN(parsed)) return parsed;
        }
    } catch {}
    return fallback;
}


const isHorizontalLayout = (optionsPairs: { text: string; imageUrl?: string | null; audioUrl?: string | null }[]) => {
  if (optionsPairs.length !== 4) return false;
  const hasMedia = optionsPairs.some(opt => opt.imageUrl || opt.audioUrl);
  if (hasMedia) return false;
  return optionsPairs.every(opt => opt.text.trim().length <= 15);
};

const DEMO_QUESTIONS: ExamQuestion[] = [
  {
    id: "demo-q1",
    examId: "demo-exam",
    scenarioId: null,
    type: "MCQ",
    questionText: "日本語の勉強は、毎日続けること（　　）大切だ。",
    options: {
      A: "が",
      B: "に",
      C: "を",
      D: "で"
    },
    correctAnswer: "A",
    marks: 2,
  },
  {
    id: "demo-q2",
    examId: "demo-exam",
    scenarioId: "demo-sc1",
    scenario: {
      id: "demo-sc1",
      instructionsText: "【読解】次の文章を読んで、後の問いに答えなさい。\n\n桜（さくら）の花は、春になると一斉に咲きます。日本人は昔から桜の花が大好きで、お花見をして楽しみます。しかし、桜の花が咲く期間はとても短く、風が吹くとすぐに散ってしまいます。",
      imageUrl: null,
      audioUrl: null,
      sortOrder: 1,
    },
    type: "MCQ",
    questionText: "日本人は昔から何をして楽しみますか。",
    options: {
      A: "お花見",
      B: "お祭り",
      C: "旅行",
      D: "読書"
    },
    correctAnswer: "A",
    marks: 3,
  },
  {
    id: "demo-q3",
    examId: "demo-exam",
    scenarioId: "demo-sc2",
    scenario: {
      id: "demo-sc2",
      instructionsText: "【聴解】スピーカーから流れる音声を聞いて、正しい絵を一つ選びなさい。",
      imageUrl: null,
      audioUrl: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3",
      sortOrder: 2,
    },
    type: "LISTENING",
    questionText: "音声の中で、男の人は何と言っていますか。",
    options: {
      A: "明日テストがあります",
      B: "昨日は休みでした",
      C: "来週日本に行きます",
      D: "日本語は難しいです"
    },
    correctAnswer: "C",
    marks: 3,
  }
];

function sortQuestionsMixed(questions: ExamQuestion[]): ExamQuestion[] {
  const standalone = questions.filter((q) => !q.scenarioId);
  const scenarioQuestions = questions.filter((q) => q.scenarioId);

  const questionsByScenario = new Map<string, ExamQuestion[]>();
  for (const q of scenarioQuestions) {
    const sid = q.scenarioId!;
    if (!questionsByScenario.has(sid)) {
      questionsByScenario.set(sid, []);
    }
    questionsByScenario.get(sid)!.push(q);
  }

  for (const [_sid, qs] of questionsByScenario.entries()) {
    qs.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  }

  type TopLevelItem =
    | { type: "scenario"; id: string; sortOrder: number; questions: ExamQuestion[] }
    | { type: "standalone"; id: string; sortOrder: number; question: ExamQuestion };

  const topLevelItems: TopLevelItem[] = [];
  const processedScenarios = new Set<string>();

  for (const q of scenarioQuestions) {
    const s = q.scenario;
    const sId = q.scenarioId!;
    if (!processedScenarios.has(sId)) {
      processedScenarios.add(sId);
      topLevelItems.push({
        type: "scenario",
        id: sId,
        sortOrder: s?.sortOrder ?? 0,
        questions: questionsByScenario.get(sId) ?? [],
      });
    }
  }

  for (const q of standalone) {
    topLevelItems.push({
      type: "standalone",
      id: q.id,
      sortOrder: q.sortOrder ?? 0,
      question: q,
    });
  }

  topLevelItems.sort((a, b) => {
    if (a.sortOrder !== b.sortOrder) {
      return a.sortOrder - b.sortOrder;
    }
    return a.id.localeCompare(b.id);
  });

  const sortedQuestions: ExamQuestion[] = [];
  for (const item of topLevelItems) {
    if (item.type === "standalone") {
      sortedQuestions.push(item.question);
    } else {
      sortedQuestions.push(...item.questions);
    }
  }

  return sortedQuestions;
}

function groupByScenario(questions: ExamQuestion[]) {
  const groups: { scenario: QuestionScenario | null; questions: ExamQuestion[] }[] = [];
  let currentGroup: { scenario: QuestionScenario | null; questions: ExamQuestion[] } | null = null;

  for (const q of questions) {
    const isScenario = !!q.scenarioId;
    if (!currentGroup) {
      currentGroup = {
        scenario: isScenario ? q.scenario ?? null : null,
        questions: [q],
      };
      groups.push(currentGroup);
    } else {
      const currentIsScenario = !!currentGroup.scenario;
      const sameScenario = isScenario && currentIsScenario && currentGroup.scenario.id === q.scenarioId;
      const bothStandalone = !isScenario && !currentIsScenario;

      if (sameScenario || bothStandalone) {
        currentGroup.questions.push(q);
      } else {
        currentGroup = {
          scenario: isScenario ? q.scenario ?? null : null,
          questions: [q],
        };
        groups.push(currentGroup);
      }
    }
  }
  return groups;
}

const audioTokenCache = new Map<string, { signedUrl: string; expiresAt: number }>();

function extractS3KeyFromUrl(urlStr: string): string | null {
  if (!urlStr) return null;
  if (urlStr.includes("X-Amz-Signature")) return null;

  if (!urlStr.startsWith('http://') && !urlStr.startsWith('https://')) {
      return decodeURIComponent(urlStr).replace(/^\//, '');
  }

  try {
    const parsed = new URL(urlStr);
    const key = decodeURIComponent(parsed.pathname).replace(/^\//, "");
    return key || null;
  } catch {
    return null;
  }
}

async function resolveSignedAudioUrl(rawUrl: string, attemptId: string): Promise<string> {
  if (!rawUrl || !attemptId) return rawUrl;
  const cacheKey = `${attemptId}:${rawUrl}`;
  const cached = audioTokenCache.get(cacheKey);
  if (cached && Date.now() < cached.expiresAt) {
    return cached.signedUrl;
  }
  const s3Key = extractS3KeyFromUrl(rawUrl);
  if (!s3Key) return rawUrl; // Can't extract key — return as-is
  try {
    const result = await getSignedAudioUrl(attemptId, s3Key);
    const CACHE_DURATION_MS = 4 * 60 * 1000; // 4 minutes (token lasts 5)
    audioTokenCache.set(cacheKey, {
      signedUrl: result.signedUrl,
      expiresAt: Date.now() + CACHE_DURATION_MS,
    });
    return result.signedUrl;
  } catch (err) {
    console.warn('[AudioToken] Failed to resolve signed URL, using raw URL as fallback:', err);
    return rawUrl;
  }
}

export default function ExamPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { user, isHydrating } = useAuth();

  const courseId = searchParams.get("courseId") ?? "";
  const examId = searchParams.get("examId") ?? "";

  const isReady = Boolean(courseId && examId);

  const myEnrollmentsQuery = useQuery({
    queryKey: ["myEnrollmentsForExamPage"],
    queryFn: getMyEnrollments,
    enabled: Boolean(user),
  });

  const profileQuery = useQuery({
    queryKey: ["profile"],
    queryFn: fetchProfile,
    enabled: Boolean(user),
  });

  const profileName = useMemo(() => {
    const p = profileQuery.data?.profile;
    if (p && (p.firstName || p.lastName)) {
      return [p.firstName, p.lastName].filter(Boolean).join(" ");
    }
    return null;
  }, [profileQuery.data]);

  const isEnrolled = useMemo(() => {
    if (!user) return false;
    if (user.backendRole === "TEACHER" || user.backendRole === "SYSTEM_ADMIN") return true;
    if (!courseId || !myEnrollmentsQuery.data) return false;
    return myEnrollmentsQuery.data.some((e) => e.course?.id === courseId);
  }, [user, courseId, myEnrollmentsQuery.data]);

  const questionsQuery = useQuery({
    queryKey: ["examQuestions", courseId, examId],
    queryFn: () => getExamQuestions(courseId, examId),
    enabled: isReady && isEnrolled,
  });

  const courseExamsQuery = useQuery({
    queryKey: ["courseExams", courseId],
    queryFn: () => getCourseExams(courseId, { page: 1, limit: 100 }),
    enabled: isReady,
  });

  const courseQuery = useQuery({
    queryKey: ["course", courseId],
    queryFn: () => getCourse(courseId),
    enabled: isReady,
  });

  const chaptersQuery = useQuery({
    queryKey: ["courseChapters", courseId],
    queryFn: () => getCourseChapters(courseId),
    enabled: isReady,
  });

  const myAttemptsQuery = useQuery({
    queryKey: ["myAttempts", courseId, examId],
    queryFn: () => getMyAttempts(courseId, examId),
    enabled: isReady && isEnrolled,
  });

  const currentExam = useMemo(() => {
    return (courseExamsQuery.data?.items ?? []).find((e) => e.id === examId) ?? null;
  }, [courseExamsQuery.data, examId]);

  const currentChapter = useMemo(() => {
    if (!currentExam?.chapterId) return null;
    return (chaptersQuery.data ?? []).find((ch) => ch.id === currentExam.chapterId) ?? null;
  }, [chaptersQuery.data, currentExam]);

  const isChapterLocked = useMemo(() => {
    if (!currentChapter) return false;
    return Boolean(currentChapter.isLocked);
  }, [currentChapter]);

  const questions = isEnrolled ? (questionsQuery.data ?? []) : DEMO_QUESTIONS;
  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [attempt, setAttempt] = useState<ExamAttempt | null>(null);
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [result, setResult] = useState<ExamAttempt | null>(null);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [showReview, setShowReview] = useState(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Audio Player State
  const [playedAudioIds, setPlayedAudioIds] = useState<string[]>([]);
  const [bypassedAudioIds, setBypassedAudioIds] = useState<string[]>([]);
  const markAudioAsPlayedForId = useCallback((id: string) => {
    const targetQ = questions.find(q => q.id === id);
    let qIdsToMark: string[] = [];
    if (targetQ) {
      if (targetQ.scenarioId) {
        qIdsToMark = questions.filter(q => q.scenarioId === targetQ.scenarioId).map(q => q.id);
      } else {
        qIdsToMark = [targetQ.id];
      }
    } else {
      qIdsToMark = questions.filter(q => q.scenarioId === id).map(q => q.id);
    }
    const nextPlayed = [...new Set([...playedAudioIds, ...qIdsToMark, id])];
    setPlayedAudioIds(nextPlayed);
    if (attemptId) {
      localStorage.setItem(`exam_attempt_${attemptId}_played_audio`, JSON.stringify(nextPlayed));
    }
  }, [questions, playedAudioIds, attemptId]);
  const bypassAudio = useCallback((id: string) => {
    const nextBypassed = [...new Set([...bypassedAudioIds, id])];
    setBypassedAudioIds(nextBypassed);
  }, [bypassedAudioIds]);

  useEffect(() => {
    if (attemptId) {
      const stored = localStorage.getItem(`exam_attempt_${attemptId}_played_audio`);
      if (stored) {
        try {
          setPlayedAudioIds(JSON.parse(stored));
        } catch { }
      }
    }
  }, [attemptId]);

  // Focus Mode State Adjustments
  const [isNavigatorOpen, setIsNavigatorOpen] = useState(false);
  const [confirmModalType, setConfirmModalType] = useState<"exit" | "submit" | null>(null);

  const reviewQuery = useQuery({
    queryKey: ["examReview", courseId, examId, result?.id],
    queryFn: async () => {
      // `DEMO_QUESTIONS` — the previous identifier here (`demoExamQuestions`) did
      // not exist, so this line threw a ReferenceError whenever questionsQuery
      // had no data. It was only reachable on opening the review panel before;
      // now that this query runs as soon as results render, it must be correct.
      if (!isEnrolled || result?.id === "fake-attempt-id") return questionsQuery.data || DEMO_QUESTIONS;
      if (!examId || !result?.id) return [];
      return getAttemptReview(courseId, examId, result.id);
    },
    // Fetched as soon as a result exists, NOT only when the review panel opens.
    // The score card also needs these questions: the plain questions endpoint
    // nulls out `correctAnswer` for students, so it is the only source that can
    // produce a correct/incorrect breakdown.
    enabled: !!examId && !!result?.id,
  });

  useEffect(() => {
    if (isHydrating) return;
    if (!user) {
      navigate(`/login?returnTo=${encodeURIComponent(window.location.pathname + window.location.search)}`);
      return;
    }
    if (myEnrollmentsQuery.isPending) return;
    if (user.backendRole === "STUDENT" && !isEnrolled) {
      toast.error("You must be enrolled in this course to view or attempt the exam.");
      navigate(`/course?courseId=${encodeURIComponent(courseId)}`);
      return;
    }
    if (user.backendRole === "STUDENT" && isChapterLocked) {
      toast.error("You must unlock this chapter to attempt the exam.");
      navigate(`/course?courseId=${encodeURIComponent(courseId)}`);
    }
  }, [isHydrating, user, myEnrollmentsQuery.isPending, isEnrolled, isChapterLocked, courseId, navigate]);

  const attemptIdParam = searchParams.get("attemptId");

  useEffect(() => {
    if (attemptIdParam && myAttemptsQuery.data) {
      const historicalAttempt = myAttemptsQuery.data.find(a => a.id === attemptIdParam);
      if (historicalAttempt) {
        setAttemptId(historicalAttempt.id);
        setAttempt(historicalAttempt);
        setResult(historicalAttempt);
        setAnswers(historicalAttempt.answers || {});
        setIsNavigatorOpen(false);
        return;
      }
    }

    setAttemptId(null);
    setAttempt(null);
    setAnswers({});
    setResult(null);
    setIsNavigatorOpen(false);
  }, [courseId, examId, attemptIdParam, myAttemptsQuery.data]);

  const sortedQuestions = useMemo(() => {
    return sortQuestionsMixed(questions);
  }, [questions]);

  const grouped = useMemo(() => {
    return groupByScenario(sortedQuestions);
  }, [sortedQuestions]);

  const questionLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    let topLevelIndex = 0;
    for (const group of grouped) {
      if (group.scenario) {
        topLevelIndex++;
        group.questions.forEach((q, qi) => {
          labels[q.id] = `${topLevelIndex}.${qi + 1}`;
        });
      } else {
        group.questions.forEach((q) => {
          topLevelIndex++;
          labels[q.id] = `${topLevelIndex}`;
        });
      }
    }
    return labels;
  }, [grouped]);

  const totalQuestions = questions.length;
  const answeredCount = useMemo(() => {
    return questions.filter((q) => isQuestionAnswered(q, answers[q.id])).length;
  }, [questions, answers]);

  const flushSave = useCallback(
    async (map: Record<string, unknown>) => {
      if (!attemptId || !isEnrolled) return;
      try {
        const payload = normalizeMcqAnswersForApi(questions, map);
        await saveExamProgress(attemptId, { answers: payload });
      } catch {
        // best-effort
      }
    },
    [attemptId, questions, isEnrolled],
  );

  const scheduleSave = useCallback(
    (map: Record<string, unknown>) => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        void flushSave(map);
      }, 1200);
    },
    [flushSave],
  );

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!attempt?.endsAt || result) return;
    const t = window.setInterval(() => setNowTick(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [attempt?.endsAt, result]);

  const secondsLeft = useMemo(() => {
    if (!attempt?.endsAt) return null;
    const end = new Date(attempt.endsAt).getTime();
    return Math.max(0, Math.floor((end - nowTick) / 1000));
  }, [attempt?.endsAt, nowTick]);

  useEffect(() => {
    if (secondsLeft === 0 && attemptId && !result) {
      toast.warning("Time has expired! Submitting your exam automatically...", { duration: 5000 });
      void handleSubmit();
    }
  }, [secondsLeft, attemptId, result]);

  const formatTimeLeft = (sec: number | null) => {
    if (sec === null) return "";
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    if (h > 0) {
      return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    }
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  async function handleStart() {
    if (!courseId || !examId) return;
    if (!isEnrolled) {
      setResult(null);
      const fakeAttempt: ExamAttempt = {
        id: "fake-attempt-id",
        userId: 0,
        examId: "demo-exam",
        score: "0",
        isPassed: false,
        attemptNumber: 1,
        startedAt: new Date().toISOString(),
        endsAt: new Date(Date.now() + 15 * 60 * 1000).toISOString(),
      };
      setAttemptId(fakeAttempt.id);
      setAttempt(fakeAttempt);
      setAnswers({});
      toast.success("Demo exam started");
      return;
    }

    try {
      setResult(null);
      const a = await startExam(courseId, examId);
      setAttemptId(a.id);
      setAttempt(a);
      const initial = (a.answers && typeof a.answers === "object" ? a.answers : {}) as Record<string, unknown>;
      const migrated = { ...initial };
      for (const q of questions) {
        if (migrated[q.id] === undefined) continue;
        migrated[q.id] = migrateLoadedMcqToIndex(q, migrated[q.id]);
      }
      setAnswers(migrated);
      toast.success(a.answers && Object.keys(a.answers).length ? "Resumed exam" : "Exam started");
    } catch (e: any) {
      toast.error(e?.message || e?.body?.message || "Could not start exam");
    }
  }

  function setAnswerForQuestion(nextMap: Record<string, unknown>) {
    setAnswers(nextMap);
    if (attemptId) scheduleSave(nextMap);
  }

  async function handleSubmit() {
    if (!attemptId) return;
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }

    if (!isEnrolled) {
      let score = 0;
      let totalMarks = 0;
      questions.forEach((q) => {
        totalMarks += q.marks;
        const ans = answers[q.id];
        let isCorrect = false;
        if (typeof ans === "number") {
          const pairs = buildMcqDisplayPairs(q, false, "");
          const matchVal = pairs[ans]?.text?.trim() || pairs[ans]?.imageUrl?.trim() || "";
          if (matchVal === q.correctAnswer?.trim() || String(ans) === q.correctAnswer?.trim()) isCorrect = true;
        } else if (typeof ans === "string" && ans.trim() === q.correctAnswer?.trim()) {
          isCorrect = true;
        }
        if (isCorrect) {
          score += q.marks;
        }
      });
      const percent = totalMarks > 0 ? (score / totalMarks) * 100 : 0;
      const isPassed = percent >= 60;

      const fakeResult: ExamAttempt = {
        id: "fake-attempt-id",
        userId: 0,
        examId: "demo-exam",
        score: `${score}/${totalMarks}`,
        isPassed,
        attemptNumber: 1,
        startedAt: attempt?.startedAt,
        submittedAt: new Date().toISOString(),
      };
      setResult(fakeResult);
      toast.success("Demo exam submitted!");
      return;
    }

    const payload = normalizeMcqAnswersForApi(questions, answers);
    await flushSave(payload);
    try {
      const submitted = await submitExam(attemptId, { answers: payload });
      setResult(submitted);
      queryClient.invalidateQueries({ queryKey: ["courseChapters", courseId] });
      queryClient.invalidateQueries({ queryKey: ["courseProgress", courseId] });
      queryClient.invalidateQueries({ queryKey: ["myProgress"] });
    } catch (e: any) {
      toast.error(e?.message || e?.body?.message || "Failed to submit exam");
    }
  }

  if (!isReady) {
    return (
      <DashboardLayout showSidebar={isEnrolled}>
        <div className="dash-page flex items-center justify-center">
          <div className="dash-empty-state">
            <p className="text-sm font-medium">Missing `courseId` or `examId`.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const backToCourse = () => navigate(`/course?courseId=${encodeURIComponent(courseId)}`);

// renderQuestionContent removed, using Shared QuestionCard



  // =========================================================================
  // 1. FOCUS MODE TAKE EXAM SCREEN (attemptId && !result)
  // =========================================================================
  if (attemptId && !result) {
    return (
      <ExamSecurityWrapper>
        <div className="h-[100dvh] bg-[#edece9] text-stone-900 font-sans flex flex-col selection:bg-stone-200 overflow-x-hidden relative">
          {/* Equalizer animation and inline styles */}
          <style dangerouslySetInnerHTML={{
            __html: `
              @keyframes eq-pulse {
                0%, 100% { transform: scaleY(0.2); }
                50% { transform: scaleY(1); }
              }
              .animate-eq-1 { animation: eq-pulse 0.6s ease-in-out infinite; }
              .animate-eq-2 { animation: eq-pulse 0.8s ease-in-out infinite; animation-delay: 0.1s; }
              .animate-eq-3 { animation: eq-pulse 0.5s ease-in-out infinite; animation-delay: 0.2s; }
              .animate-eq-4 { animation: eq-pulse 0.7s ease-in-out infinite; animation-delay: 0.15s; }
              .animate-eq-5 { animation: eq-pulse 0.9s ease-in-out infinite; animation-delay: 0.25s; }
            ` }} />
  


        {/* Simple Academic Focus Header */}
        <header className="h-14 shrink-0 border-b border-stone-300 bg-white/95 backdrop-blur-xs px-6 flex items-center justify-between sticky top-0 z-30 select-none shadow-xs">
          <div className="flex items-center gap-2 md:gap-3 truncate min-w-0">
            <span className="font-bold text-[10px] sm:text-sm tracking-wide text-stone-900 whitespace-nowrap shrink-0">
              {courseQuery.data?.title || "日本語能力試験 (JLPT)"}
            </span>
            <span className="text-[10px] sm:text-xs text-stone-300 shrink-0">|</span>
            <span className="text-[9px] sm:text-xs font-semibold text-stone-600 uppercase tracking-widest font-sans truncate">
              {currentExam?.title || "CHAPTER ASSESSMENT"}
            </span>
          </div>

          <div className="flex items-center gap-2 md:gap-4">
            {secondsLeft !== null && (
              <span className={cn(
                "inline-flex items-center gap-1 md:gap-2 font-mono text-sm md:text-base font-bold tabular-nums transition-colors",
                secondsLeft < 300 ? "text-red-600 animate-pulse font-extrabold" : "text-stone-700"
              )}>
                <Clock className="size-4 shrink-0" />
                <span className="hidden sm:inline">[ {formatTimeLeft(secondsLeft)} ]</span>
                <span className="sm:hidden">{formatTimeLeft(secondsLeft)}</span>
              </span>
            )}
            <span className="text-xs font-semibold text-stone-500 font-sans">
              <span className="hidden md:inline">[ </span>{answeredCount}<span className="hidden md:inline"> / {totalQuestions} 解答済み</span><span className="md:hidden">/{totalQuestions}</span><span className="hidden md:inline"> ]</span>
            </span>

            <Button
              variant="outline"
              size="sm"
              className="text-stone-600 border-stone-300 hover:bg-stone-50 text-xs h-8 px-2 md:px-3 font-sans font-medium"
              onClick={() => setConfirmModalType("exit")}
            >
              <ArrowLeft size={14} className="sm:mr-1.5" /> <span className="hidden sm:inline">一時退室</span>
            </Button>

          </div>
        </header>


        {/* CBT Card-Based Workspace Container */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-[#f4f3f0]" id="questions-scroll-container">
          <div className="space-y-6 max-w-4xl mx-auto">
            <div className="space-y-6 sm:space-y-8">
              {grouped.map((group, gi) => {
                const groupLabel = group.questions[0] ? questionLabels[group.questions[0].id]?.split('.')[0] || "" : "";

                return (
                  <div key={gi} className="space-y-2">
                    {/* Scenario Passage Section */}
                    {group.scenario ? (
                      (() => {
                        const scenarioId = group.scenario.id || `scenario_${gi}`;
                        const isScenarioPlayed = playedAudioIds.includes(scenarioId);
                        const isBypassed = bypassedAudioIds.includes(scenarioId);
                        const shouldBlur = group.scenario.audioUrl && !isScenarioPlayed && !isBypassed;
                        return (
                          <ScenarioBlock
                            scenario={group.scenario}
                            questionCount={group.questions.length}
                            audioElement={
                              group.scenario.audioUrl ? (
                                <CustomSecureAudioPlayer
                                    id={scenarioId}
                                    attemptId={attemptId}
                                    url={group.scenario.audioUrl}
                                    resolveUrl={attemptId ? (raw) => resolveSignedAudioUrl(raw, attemptId) : undefined}
                                    playLimit={getPlayLimitFromUrl(group.scenario.audioUrl, 0)}
                                    onEnded={() => markAudioAsPlayedForId(scenarioId)}
                                />
                              ) : undefined
                            }
                          >
                            {group.questions.map((q, qi) => {
                              const optPairs = buildMcqDisplayPairs(q, true, `${attemptId}:${q.id}:mcqopts`);
                              const qLabel = questionLabels[q.id] || "";
                              
                              const qAudioUrl = q.audioUrl || null;
                              const isPlayed = playedAudioIds.includes(q.id);
                              const isQBypassed = bypassedAudioIds.includes(q.id);
                              const shouldBlurQ = qAudioUrl && !isPlayed && !isQBypassed;

                              return (
                                <div key={q.id} id={`q-${q.id}`} className="relative space-y-4 scroll-mt-24">
                                      <QuestionCard
                                          q={q}
                                          questionLabel={qLabel}
                                          marks={q.marks || 0}
                                          selectedAnswer={answers[q.id]}
                                          onSelectAnswer={(qid, idx) => setAnswerForQuestion({ ...answers, [qid]: idx })}
                                          optPairs={optPairs}
                                          audioElement={qAudioUrl ? (
                                              <CustomSecureAudioPlayer
                                                  id={q.id}
                                                  attemptId={attemptId}
                                                  url={qAudioUrl}
                                                  resolveUrl={attemptId ? (raw) => resolveSignedAudioUrl(raw, attemptId) : undefined}
                                                  playLimit={getPlayLimitFromUrl(qAudioUrl, 0)}
                                                  onEnded={() => markAudioAsPlayedForId(q.id)}
                                              />
                                          ) : undefined}
                                      />
                                </div>
                              );
                            })}
                          </ScenarioBlock>
                        );
                      })()
                    ) : null}


                    {/* Non-Scenario Question items */}
                    {!group.scenario && (
                    <div className="space-y-4">
                          {group.questions.map((q, qi) => {
                            const optPairs = buildMcqDisplayPairs(q, true, `${attemptId}:${q.id}:mcqopts`);
                            const qLabel = questionLabels[q.id] || "";
                            const useHorizontal = isHorizontalLayout(optPairs);
                            
                            const qAudioUrl = q.audioUrl || null;
                            const isPlayed = playedAudioIds.includes(q.id);
                            const isBypassed = bypassedAudioIds.includes(q.id);
                            const shouldBlur = qAudioUrl && !isPlayed && !isBypassed;
    
                            return (
                              <div key={q.id} id={`q-${q.id}`} className="relative space-y-4 scroll-mt-24">
                                            <QuestionCard
                                                q={q}
                                                questionLabel={qLabel}
                                                marks={q.marks || 0}
                                                selectedAnswer={answers[q.id]}
                                                onSelectAnswer={(qid, idx) => setAnswerForQuestion({ ...answers, [qid]: idx })}
                                                optPairs={optPairs}
                                                audioElement={qAudioUrl ? (
                                                    <CustomSecureAudioPlayer
                                                        id={q.id}
                                                        attemptId={attemptId}
                                                        url={qAudioUrl}
                                                        resolveUrl={attemptId ? (raw) => resolveSignedAudioUrl(raw, attemptId) : undefined}
                                                        playLimit={getPlayLimitFromUrl(qAudioUrl, 0)}
                                                        onEnded={() => markAudioAsPlayedForId(q.id)}
                                                    />
                                                ) : undefined}
                                            />
                              </div>
                            );
                          })}
                        </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Submission Block — Card Style */}
            <div className="mt-4 bg-white rounded-2xl border border-stone-200 shadow-sm p-6 sm:p-8 flex flex-col items-center gap-4 text-center select-none font-sans">
              <p className="text-sm text-stone-600 font-medium">
                {answeredCount < totalQuestions
                  ? `未解答の問題が ${totalQuestions - answeredCount} 問残っています。`
                  : "すべての問題に解答しました。解答用紙を確認して、提出してください。"}
              </p>
              <Button
                size="lg"
                className="bg-slate-900 hover:bg-slate-800 text-white font-bold px-8 py-3 rounded-xl transition-all text-sm font-sans shadow-md"
                onClick={() => setConfirmModalType("submit")}
              >
                試験を終了して提出する / Submit Examination
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Question Progress / Footer */}
        <div className="md:hidden h-16 border-t border-stone-300 bg-white flex items-center justify-between px-4 shrink-0 relative z-30 shadow-[0_-4px_10px_rgba(0,0,0,0.03)]">
          <div className="flex flex-col justify-center">
            <span className="text-[9px] text-stone-500 font-bold uppercase tracking-wider leading-none mb-0.5">
              試験の進捗
            </span>
            <span className="text-xs font-bold text-stone-900 font-mono leading-none">
              {answeredCount} / {totalQuestions}
            </span>
          </div>

        </div>
        {confirmModalType && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/55 backdrop-blur-xs p-4 animate-in fade-in duration-200 select-none font-sans">
            <div className="w-full max-w-md bg-white border border-stone-300 p-6 shadow-xl rounded-2xl space-y-6">
              <div className="space-y-2">
                <h3 className="text-lg font-bold text-stone-900">
                  {confirmModalType === "exit" ? "一時退室しますか？ / Pause and exit?" : "試験を提出しますか？ / Submit the exam?"}
                </h3>
                <p className="text-sm text-stone-500 leading-relaxed">
                  {confirmModalType === "exit"
                    ? "退室しても解答は保存されます。後で再開することができます。"
                    : "試験を終了して解答を提出します。提出後は解答の変更はできません。本当によろしいですか？"}
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmModalType(null)}
                  className="font-semibold border-stone-300 text-stone-700 rounded-xl h-9 hover:bg-stone-50"
                >
                  キャンセル / Cancel
                </Button>
                <Button
                  type="button"
                  variant={confirmModalType === "submit" ? "destructive" : "default"}
                  size="sm"
                  onClick={() => {
                    const type = confirmModalType;
                    setConfirmModalType(null);
                    if (type === "exit") {
                      backToCourse();
                    } else {
                      void handleSubmit();
                    }
                  }}
                  className="font-bold rounded-xl"
                >
                  {confirmModalType === "submit" ? "試験提出 / Submit" : "退室 / Exit"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      </ExamSecurityWrapper>
    );
  }

  // =========================================================================
  // 2. DASHBOARD SHELL LAYOUT RENDERS (COVER PAGE & RESULT REPORT)
  // =========================================================================
  return (
    <DashboardLayout showSidebar={isEnrolled}>
      <div className="w-full p-6 md:p-10 max-w-6xl mx-auto space-y-6">
        <div className="w-full flex flex-col items-center">

          {/* SKELETON LOADING VIEW */}
          {isEnrolled && questionsQuery.isPending ? (
            <div className="bg-white border border-stone-300 p-8 md:p-12 shadow-sm space-y-6 w-full max-w-3xl">
              <Skeleton className="h-10 w-2/3 bg-stone-100 rounded-none" />
              <div className="border border-stone-200 p-4 space-y-2">
                <Skeleton className="h-4 w-full bg-stone-100 rounded-none" />
                <Skeleton className="h-4 w-5/6 bg-stone-100 rounded-none" />
              </div>
              <Skeleton className="h-40 w-full bg-stone-100 rounded-none" />
              <Skeleton className="h-32 w-full bg-stone-100 rounded-none" />
            </div>
          ) : result ? (
            <>
              {(() => {
                if (!currentExam) return null;
                
                const totalScore = currentExam.totalMarks;
                const obtainedScore = Number(result.score);
                const pct = totalScore > 0 ? Math.round((obtainedScore / totalScore) * 100) : 0;
                const overallPassMark = currentExam.passMarks || Math.ceil(totalScore * 0.6);
                
                const typeScores: Record<string, { correct: number, incorrect: number, skipped: number, score: number, maxScore: number }> = {};

                let answeredCount = 0;
                let skippedCount = 0;

                // Grade against the review payload, which is the only response that
                // carries `correctAnswer` (the questions endpoint strips it for
                // students so the paper can't be leaked mid-attempt). Falling back
                // to `questions` keeps totals right while the review request is in
                // flight — those rows just can't contribute correct/incorrect.
                const gradedQuestions = reviewQuery.data?.length
                    ? (reviewQuery.data as typeof questions)
                    : questions;
                const canGrade = Boolean(reviewQuery.data?.length);

                gradedQuestions.forEach(q => {
                    const ans = result.answers?.[q.id];
                    if (ans !== undefined && ans !== "") answeredCount++;
                    else skippedCount++;
                    
                    if (!typeScores[q.type]) {
                        typeScores[q.type] = { correct: 0, incorrect: 0, skipped: 0, score: 0, maxScore: 0 };
                    }
                    typeScores[q.type].maxScore += q.marks;
                    
                    if (q.type === "MCQ") {
                         if (ans === undefined || ans === null || ans === "") {
                             typeScores[q.type].skipped++;
                         } else {
                             // Robust correctness check matching ExamReviewPanel.getAnswerState logic
                             const userAnsStr = String(ans).trim();
                             const correctAnsStr = String(q.correctAnswer ?? "").trim();
                             let isCorrect = userAnsStr === correctAnsStr;

                             // Fallback: match by option id or option text
                             if (!isCorrect && q.options && typeof q.options === "object") {
                                 const optionsData = q.options as any;
                                 const optsArray = optionsData.options || optionsData;
                                 if (Array.isArray(optsArray)) {
                                     const correctOption = optsArray.find((o: any) => o.id === correctAnsStr);
                                     if (correctOption) {
                                         const matchVal = String(correctOption.text || correctOption.imageUrl || "").trim();
                                         if (matchVal === userAnsStr) isCorrect = true;
                                     }
                                 }
                             }

                             if (isCorrect) {
                                 typeScores[q.type].correct++;
                                 typeScores[q.type].score += q.marks;
                             } else {
                                 typeScores[q.type].incorrect++;
                             }
                         }
                    } else {
                        // Backend calculates partial marking or full marks, we will just use overall score for non-MCQ
                    }
                });
                
                let mappedSections: any[] = [];
                const hasOnlyMcq = gradedQuestions.every(q => q.type === "MCQ");

                if (hasOnlyMcq) {
                    Object.entries(typeScores).forEach(([type, stats]) => {
                         if (stats.maxScore > 0) {
                             mappedSections.push({
                                 name: type === "MCQ" ? "Multiple Choice Questions" : type,
                                 originalName: type,
                                 // Only show a breakdown once the review data has landed.
                                 // Before that the counts would all read 0 and the grid
                                 // would claim every answer was unanswered.
                                 correct: canGrade ? stats.correct : 0,
                                 incorrect: canGrade ? stats.incorrect : 0,
                                 skipped: canGrade ? stats.skipped : 0,
                                 // Score stays anchored to the backend's authoritative
                                 // figure rather than the client's re-grade, so the
                                 // headline number can never disagree with the server.
                                 score: obtainedScore,
                                 maxScore: stats.maxScore,
                                 percentage: stats.maxScore > 0
                                     ? Math.round((obtainedScore / stats.maxScore) * 100)
                                     : 0,
                             });
                         }
                    });
                } else {
                    mappedSections.push({
                         name: "General Assessment",
                         originalName: "GENERAL",
                         correct: 0,
                         incorrect: 0,
                         skipped: 0,
                         score: obtainedScore,
                         maxScore: totalScore,
                         percentage: pct
                    });
                }
                
                const timeTakenMinutes = result.startedAt && result.submittedAt
                    ? Math.round((new Date(result.submittedAt).getTime() - new Date(result.startedAt).getTime()) / 60000)
                    : null;
                
                return (
                    <div className="w-full flex flex-col items-center">
                        <ExamResultReport
                            examName={currentExam.title || "Course Exam"}
                            examTypeLabel="Chapter Exam"
                        candidateId={result.id}
                        candidateName={profileName || user?.email || "Student"}
                        completionDate={new Date(result.submittedAt || new Date())}
                        isPassed={result.isPassed}
                        totalScore={totalScore}
                        obtainedScore={obtainedScore}
                        percentage={pct}
                        passingScore={overallPassMark}
                        timeTakenMinutes={timeTakenMinutes}
                        totalQuestions={questions.length}
                        answeredCount={answeredCount}
                        skippedCount={skippedCount}
                        overallAccuracy={pct}
                        sections={mappedSections}
                        actions={
                            <>
                                <Button
                                    className="bg-stone-900 hover:bg-stone-800 text-white rounded-xl px-6 text-xs h-10 font-bold border border-stone-950 shadow-xs"
                                    onClick={() => setShowReview(true)}
                                >
                                    解答解説を見る (Review Answers)
                                </Button>
                                <Button
                                    variant="outline"
                                    className="border-stone-300 text-stone-700 rounded-xl px-6 text-xs h-10 font-bold hover:bg-stone-50"
                                    onClick={backToCourse}
                                >
                                    コースに戻る (Return to Course)
                                </Button>
                            </>
                        }
                    />
                  </div>
                );
              })()}

              {/* Fullscreen review overlay */}
              {showReview && (
                <div className="fixed inset-0 z-50 bg-[#f4f3f0] overflow-y-auto animate-in fade-in duration-200">
                  {reviewQuery.isLoading ? (
                    <div className="flex h-full items-center justify-center">
                      <div className="animate-spin w-8 h-8 border-4 border-stone-800 border-t-transparent rounded-full" />
                    </div>
                  ) : reviewQuery.data ? (
                    <ExamReviewPanel
                      questions={sortQuestionsMixed((reviewQuery.data || questionsQuery.data) as any)}
                      answers={(result?.answers as Record<string, unknown>) || answers}
                      attemptId={result?.id}
                      title="試験 解答解説 / Exam Answer Review"
                      onClose={() => setShowReview(false)}
                      questionLabels={questionLabels}
                    />
                  ) : (
                    <div className="flex h-full flex-col items-center justify-center p-4">
                      <p className="text-red-600 mb-4">Could not load review data. {reviewQuery.error ? String(reviewQuery.error) : ""}</p>
                      <Button onClick={() => setShowReview(false)}>Close</Button>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (

            // =========================================================================
            // COVER PAGE: JLPT Exam Booklet Cover (試験問題冊子)
            // =========================================================================
            <div
              className="mx-auto max-w-2xl bg-white border border-stone-300 p-8 md:p-14 shadow-md relative text-stone-900 flex flex-col"
              
            >
              {/* Margins Border Frame decoration */}
              <div className="absolute inset-2 border border-stone-200 pointer-events-none opacity-40 select-none rounded-xs" />

              <div className="text-center space-y-4 select-none mb-10">
                <div className="border-2 border-stone-800 p-4 inline-block tracking-widest text-lg font-bold">
                  日本語能力試験
                </div>
                <h1 className="text-xl md:text-2xl font-bold tracking-widest pt-2">
                  {currentExam?.title || "模擬試験"}
                </h1>
                <p className="text-xs uppercase tracking-widest text-stone-500 font-sans">
                  JAPANESE LANGUAGE PROFICIENCY TEST
                </p>
              </div>

              {/* Examinee Registration Info Box Placeholder */}
              <div className="border border-stone-400 overflow-hidden text-sm font-sans mb-8 select-none">
                <div className="grid grid-cols-3 divide-x divide-stone-400">
                  <div className="col-span-1 bg-stone-50 p-3 font-semibold text-stone-600 text-center flex items-center justify-center">
                    受験番号<br />Examinee ID
                  </div>
                  <div className="col-span-2 p-3 font-semibold text-stone-800 flex items-center justify-center bg-white font-mono">
                    [ Auto-Assigned ]
                  </div>
                </div>
                <div className="grid grid-cols-3 divide-x divide-stone-400 border-t border-stone-400">
                  <div className="col-span-1 bg-stone-50 p-3 font-semibold text-stone-600 text-center flex items-center justify-center">
                    氏名<br />Examinee Name
                  </div>
                  <div className="col-span-2 p-3 font-semibold text-stone-850 flex items-center justify-center bg-white">
                    {profileName || user?.email || "Guest Student"}
                  </div>
                </div>
              </div>

              {/* Retry Limits Logic */}
              {(() => {
                const maxAttempts = currentExam?.maxAttempts ?? 3;
                const attempts = myAttemptsQuery.data ?? [];
                const submittedAttempts = attempts.filter(a => a.submittedAt !== null);
                const hasPassed = submittedAttempts.some(a => a.isPassed);
                const isMaxAttemptsReached = !hasPassed && submittedAttempts.length >= maxAttempts;
                
                if (hasPassed) {
                  return (
                    <div className="border border-green-300 bg-green-50 p-6 space-y-4 text-center font-sans mb-8 select-none">
                      <div className="mx-auto w-12 h-12 rounded-full bg-green-100 flex items-center justify-center text-green-700">
                        <CheckCircle2 size={24} />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-green-800">You have passed this exam!</h3>
                        <p className="text-sm text-green-700 mt-1">You may review your previous attempts or continue to the next chapter. You can also retake the exam for practice.</p>
                      </div>
                    </div>
                  );
                }

                if (isMaxAttemptsReached) {
                  return (
                    <div className="border border-red-300 bg-red-50 p-6 space-y-5 font-sans mb-8 select-none text-center">
                      <div className="mx-auto w-12 h-12 rounded-full bg-red-100 flex items-center justify-center text-red-700">
                        <XCircle size={24} />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-red-800">Maximum Attempts Reached</h3>
                        <p className="text-sm text-red-700 mt-2">
                          You have reached the maximum number of exam attempts ({maxAttempts}) for this chapter. 
                          To unlock the exam again, please review and complete all lessons in this chapter.
                        </p>
                      </div>
                      <Button
                        variant="destructive"
                        onClick={async () => {
                          if (!currentChapter) return;
                          try {
                            await resetChapterProgress(courseId, currentChapter.id);
                            toast.success("Chapter progress reset. Please complete the lessons again.");
                            navigate(`/lesson?courseId=${encodeURIComponent(courseId)}&chapterId=${encodeURIComponent(currentChapter.id)}`);
                          } catch (err) {
                            toast.error("Failed to reset chapter progress.");
                          }
                        }}
                      >
                        Reset Progress & Restart Chapter
                      </Button>
                    </div>
                  );
                }

                if (isEnrolled && currentExam) {
                  return (
                    <div className="border border-amber-200 bg-amber-50 p-4 font-sans mb-8 select-none text-amber-800 text-center text-sm flex flex-col items-center gap-2">
                      <div className="flex items-center justify-center gap-2 font-bold">
                        <AlertCircle size={16} />
                        Attempts Remaining: {maxAttempts - submittedAttempts.length} of {maxAttempts}
                      </div>
                    </div>
                  );
                }

                return null;
              })()}

              {/* Exam Instructions Panel */}
              <div className="border border-stone-300 p-6 space-y-6 text-xs text-stone-700 font-sans leading-relaxed select-none mb-8">
                <h3 className="text-sm font-bold text-stone-900 border-b border-stone-200 pb-2 text-center">
                  受験者注意事項 / INSTRUCTIONS
                </h3>
                <ul className="list-decimal pl-5 space-y-3">
                  <li>
                    <strong>「試験を開始する」</strong>ボタンをクリックするまで、試験問題を開始しないでください。<br />
                    Do not start the examination until clicking the <strong>"Start Exam"</strong> button.
                  </li>
                  <li>
                    試験中にブラウザを閉じたりページから離脱したりしても、選択済みの解答は自動的に保存され、再開することができます。<br />
                    You can leave and resume; answers are continuously synced and saved in the background.
                  </li>
                  <li>
                    すべての問題は1つのページに表示されます。解答はすべて選択式です。<br />
                    All questions are displayed on one continuous page. Answers are select-only.
                  </li>
                  <li>
                    解答をすべて確認した上で、最後の提出ボタンを押してください。<br />
                    Review all answers carefully before submitting the exam.
                  </li>
                </ul>
              </div>

              {/* Start Buttons */}
              {(() => {
                const maxAttempts = currentExam?.maxAttempts ?? 3;
                const attempts = myAttemptsQuery.data ?? [];
                const submittedAttempts = attempts.filter(a => a.submittedAt !== null);
                const hasPassed = submittedAttempts.some(a => a.isPassed);
                const isMaxAttemptsReached = !hasPassed && submittedAttempts.length >= maxAttempts;
                const isDisabled = (isEnrolled && questionsQuery.isPending) || isMaxAttemptsReached;

                return (
                  <div className="flex flex-col-reverse gap-3 border-t border-stone-200 pt-6 sm:flex-row sm:justify-end select-none font-sans">
                    <Button
                      variant="outline"
                      className="border-stone-300 text-stone-700 rounded-none h-11 px-6 font-medium text-sm"
                      onClick={backToCourse}
                    >
                      キャンセル / Cancel
                    </Button>
                    <Button
                      className="bg-stone-900 hover:bg-stone-850 text-white rounded-none border border-stone-950 h-11 px-8 font-bold text-sm min-w-[160px]"
                      onClick={() => void handleStart()}
                      disabled={isDisabled}
                    >
                      {isEnrolled && questionsQuery.isPending ? (
                        <>
                          <Loader2 className="mr-2 size-4 animate-spin" /> 読み込み中…
                        </>
                      ) : isMaxAttemptsReached ? (
                        "上限に達しました / Max Attempts Reached"
                      ) : hasPassed ? (
                        "再試験 (練習) / Retake for Practice"
                      ) : (
                        "試験を開始する / Start Exam"
                      )}
                    </Button>
                  </div>
                );
              })()}
            </div>
          )}

        </div>



      </div>
    </DashboardLayout>
  );
}
